import React from 'react';
import { Grid, Skeleton, styled, Typography, useTheme } from '@mui/material';

import { useAppSelector } from '../../state/hooks';
import { ApiStatus } from '../../utils/enums';
import { formatCurrency } from '../../utils/formattingUtils';
import Revenue from '../Widgets/Revenue';
import AverageRate from '../Widgets/AverageRate';
import Transit from '../Widgets/Transit';
import ShipperDetailsMap from '../Map/ShipperDetailsMap';
import Volume from '../Widgets/Volume';

const classesPrefix = 'shipperDetails';

const classes = {
    statContainer: `${classesPrefix}-statContainer`,
    revenueContainer: `${classesPrefix}-revenueContainer`,
    rateContainer: `${classesPrefix}-rateContainer`,
    volumeContainer: `${classesPrefix}-volumeContainer`,
    labelWrapper: `${classesPrefix}-labelWrapper`,
    label: `${classesPrefix}-label`,
    mapContainer: `${classesPrefix}-mapContainer`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`& .${classes.statContainer}`]: {
            padding: theme.spacing(2),
            marginBottom: 0,
            display: 'flex',
            alignItems: 'stretch',
            order: 1,
        },
        [`& .${classes.revenueContainer}`]: {
            order: 1,
        },
        [`& .${classes.rateContainer}`]: {
            [theme.breakpoints.down('lg')]: {
                order: 3,
            },
        },
        [`& .${classes.volumeContainer}`]: {
            [theme.breakpoints.down('lg')]: {
                order: 3,
            },
        },
        [`& .${classes.labelWrapper}`]: {
            display: 'flex',
        },
        [`& .${classes.label}`]: {
            display: 'flex',
            flexGrow: 1,
        },
        [`& .${classes.mapContainer}`]: {
            display: 'flex',
            flexDirection: 'column',
            order: 2,
            [theme.breakpoints.down('lg')]: {
                order: 1,
            },
        },
    };
});

const ShipperDetails = (): React.JSX.Element => {
    const theme = useTheme();

    const shipperDetailsApiStatus = useAppSelector((state) => { return state.shipper.shipperDetailsApiStatus; });
    const shipperDetails = useAppSelector((state) => { return state.shipper.shipperDetails; });

    const ordersApiStatus = useAppSelector((state) => { return state.shipper.ordersApiStatus; });
    const orders = useAppSelector((state) => { return state.shipper.orders; });

    return (
        <StyledGrid container>
            <Grid container>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg
                    className={`${classes.statContainer} ${classes.revenueContainer}`}
                >
                    <Revenue
                        loading={shipperDetailsApiStatus === ApiStatus.Idle || shipperDetailsApiStatus === ApiStatus.Loading}
                        totalRevenue={shipperDetails.total_revenue_usd_amount}
                        linehaulRevenue={shipperDetails.linehaul_revenue_usd_amount}
                        fuelRevenue={shipperDetails.fuel_surcharge_usd_amount}
                        accessorialRevenue={shipperDetails.accessorial_revenue_usd_amount}
                        orderVolume={shipperDetails.freight_order_count}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg
                    className={`${classes.statContainer} ${classes.rateContainer}`}
                >
                    <AverageRate
                        loading={shipperDetailsApiStatus === ApiStatus.Idle || shipperDetailsApiStatus === ApiStatus.Loading}
                        totalRevenue={shipperDetails.total_revenue_usd_amount}
                        linehaulRevenue={shipperDetails.linehaul_revenue_usd_amount}
                        fuelRevenue={shipperDetails.fuel_surcharge_usd_amount}
                        accessorialRevenue={shipperDetails.accessorial_revenue_usd_amount}
                        loadedMiles={shipperDetails.loaded_distance_miles_amount}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg
                    className={`${classes.statContainer} ${classes.volumeContainer}`}
                >
                    <Grid
                        container
                        direction='column'
                        sx={{
                            alignItems: 'stretch',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Grid item xs>
                            <Volume
                                loading={shipperDetailsApiStatus === ApiStatus.Loading || shipperDetailsApiStatus === ApiStatus.Idle}
                                orderVolume={shipperDetails.freight_order_count}
                                showDailyVolume={true}
                            />
                        </Grid>
                        <Grid item>
                            <Typography variant='body2' gutterBottom className={classes.labelWrapper}>
                                <span className={classes.label}>Avg. Shipper Yield</span>
                                <span>
                                    {
                                        shipperDetailsApiStatus === ApiStatus.Loading || shipperDetailsApiStatus === ApiStatus.Idle ? (
                                            <Skeleton />
                                        ) : (
                                            formatCurrency(shipperDetails.average_pickup_party_order_yield_usd, 0, 0)
                                        )
                                    }
                                </span>
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    className={classes.mapContainer}
                >
                    <ShipperDetailsMap
                        isFetchingData={ordersApiStatus === ApiStatus.Loading || ordersApiStatus === ApiStatus.Idle}
                        theme={theme}
                        orders={orders}
                    />
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12} md lg={8} className={classes.statContainer}>
                    <Transit
                        loading={shipperDetailsApiStatus === ApiStatus.Loading || shipperDetailsApiStatus === ApiStatus.Idle}
                        orderVolume={shipperDetails.freight_order_count}
                        transitHours={shipperDetails.first_stop_departure_to_final_stop_arrival_hours_duration}
                        dwellTime={shipperDetails.netwise_dwell_hours_duration}
                        inTransitDelayHours={shipperDetails.netwise_in_transit_delay_hours_duration}
                        emptyMiles={shipperDetails.empty_distance_miles_amount}
                        loadedMiles={shipperDetails.loaded_distance_miles_amount}
                        loadTime={shipperDetails.load_hours_duration}
                        unloadTime={shipperDetails.unload_hours_duration}
                        totalRevenue={shipperDetails.total_revenue_usd_amount}
                        totalMiles={shipperDetails.travel_distance_miles_amount}
                        emptyHours={shipperDetails.empty_transit_hours_duration}
                    />
                </Grid>
            </Grid>
        </StyledGrid>
    );
};

export default ShipperDetails;
